import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import {
  Gauge, RefreshCw, ExternalLink, ChevronDown, AlertTriangle, Monitor, Smartphone, HelpCircle,
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { apiService } from '../services/api';

// Google Core Web Vitals bands. These are the published thresholds, not our own
// invention — "good" is what Google counts towards search ranking.
const BANDS = {
  lcp_ms: { good: 2500, poor: 4000 },
  fcp_ms: { good: 1800, poor: 3000 },
  cls: { good: 0.1, poor: 0.25 },
};

function band(metric, value) {
  const b = BANDS[metric];
  if (!b || value == null) return 'unknown';
  if (value <= b.good) return 'good';
  if (value <= b.poor) return 'ok';
  return 'poor';
}

const BAND_TEXT = {
  good: 'text-success-600',
  ok: 'text-warning-600',
  poor: 'text-error-600',
  unknown: 'text-gray-400',
};

const BAND_PILL = {
  good: 'bg-success-50 text-success-700',
  ok: 'bg-warning-50 text-warning-700',
  poor: 'bg-error-50 text-error-700',
  unknown: 'bg-gray-100 text-gray-500',
};

const BAND_LABEL = { good: 'Good', ok: 'Needs work', poor: 'Poor', unknown: 'No data' };

function ms(value) {
  if (value == null) return '—';
  if (value < 1000) return `${Math.round(value)}ms`;
  return `${(value / 1000).toFixed(1)}s`;
}

function kb(value) {
  if (value == null) return '—';
  if (value >= 1024) return `${(value / 1024).toFixed(1)} MB`;
  return `${Math.round(value)} KB`;
}

// Turns a URL into something a non-technical reader can place. "the JavaScript
// code that runs the page", "a photo (FEN-0373152506.jpg)" — never a raw path.
function friendlyFile(url) {
  if (!url) return 'an unknown file';
  const name = String(url).split('/').pop().split('?')[0];
  const ext = (name.split('.').pop() || '').toLowerCase();
  if (ext === 'js') return 'the JavaScript code that runs the page';
  if (ext === 'css') return 'the stylesheet that lays the page out';
  if (['woff', 'woff2', 'ttf', 'otf'].includes(ext)) return 'a font file';
  if (['jpg', 'jpeg', 'png', 'webp', 'avif', 'gif', 'svg'].includes(ext)) return `an image (${name})`;
  if (['mp4', 'webm', 'mov'].includes(ext)) return `a video (${name})`;
  return `a file (${name})`;
}

/**
 * Hover definition explaining what the metric means AND what it looked at on
 * this particular page — the only way the number becomes actionable.
 *
 * Rendered through a portal with fixed positioning rather than absolutely
 * inside the card: the card needs `overflow-hidden` for its rounded expanded
 * panel, and that clips any absolutely-positioned child. The portal escapes
 * every ancestor's overflow and stacking context. Coordinates are flipped when
 * the panel would run off the right or bottom edge of the window.
 */
const TIP_WIDTH = 320;

function Explain({ children }) {
  const [pos, setPos] = useState(null);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  // The panel stays open while the pointer is over EITHER the icon or the panel
  // itself, so its text can be read, selected and copied. The short delay covers
  // the gap the pointer crosses between the two.
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const show = () => {
    cancelClose();
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const margin = 12;
    const left = Math.min(Math.max(margin, r.left), window.innerWidth - TIP_WIDTH - margin);
    // Prefer below the icon; flip above when there isn't room.
    const below = r.bottom + 8;
    const flip = below + 180 > window.innerHeight && r.top > 200;
    setPos({ left, top: flip ? undefined : below, bottom: flip ? window.innerHeight - r.top + 8 : undefined });
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setPos(null), 180);
  };

  useEffect(() => cancelClose, []);

  return (
    <>
      <span
        ref={ref}
        className="inline-flex align-middle"
        onMouseEnter={show}
        onMouseLeave={scheduleClose}
        onFocus={show}
        onBlur={scheduleClose}
        tabIndex={0}
      >
        <HelpCircle className="h-3 w-3 text-gray-300 hover:text-primary-500 cursor-help" />
      </span>
      {pos &&
        createPortal(
          <div
            role="tooltip"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            style={{ position: 'fixed', left: pos.left, top: pos.top, bottom: pos.bottom, width: TIP_WIDTH }}
            className="z-[100] rounded-lg bg-gray-900 text-gray-100 text-xs
                       leading-relaxed p-3 shadow-2xl max-h-[70vh] overflow-y-auto"
          >
            {children}
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * One headline number: a plain-English label, the full technical name spelled
 * out underneath, the value in its Core Web Vitals colour, and — where we know
 * it — the element on this page that the number actually refers to.
 */
function Metric({ label, term, value, metric, hint, definition, measured, format: fmt = ms }) {
  const b = metric ? band(metric, value) : 'unknown';
  return (
    <div className="min-w-0">
      <div className="text-xs text-gray-600 font-medium flex items-start gap-1">
        <span>{label}</span>
        {definition && <Explain>{definition}</Explain>}
      </div>
      {term && <div className="text-[10px] text-gray-400 uppercase tracking-wide">{term}</div>}
      <div className={`text-lg font-semibold mt-0.5 ${metric ? BAND_TEXT[b] : 'text-gray-800'}`}>
        {fmt(value)}
      </div>
      {hint && <div className="text-[11px] text-gray-400">{hint}</div>}
      {measured && (
        <div className="text-[11px] text-gray-500 mt-1 break-words" title={measured}>
          <span className="text-gray-400">measured: </span>
          {measured.length > 64 ? `${measured.slice(0, 64)}…` : measured}
        </div>
      )}
    </div>
  );
}

// The five headline metrics, charted together and switchable from the legend.
// A page's story is usually the RELATIONSHIP between two of them — readiness
// against the load event on Connect, for instance — so they belong on one axis.
//
// Names here are deliberately the same standard ones used on the cards above.
// "Looks loaded" and "First product" meant nothing beside a card labelled
// "Largest Contentful Paint".
const TREND_SERIES = [
  { key: 'content_ms', name: 'Content readiness', color: '#2563eb', axis: 'time' },
  { key: 'lcp_ms', name: 'Largest Contentful Paint', color: '#7c3aed', axis: 'time' },
  { key: 'fcp_ms', name: 'First Contentful Paint', color: '#16a34a', axis: 'time' },
  { key: 'load_ms', name: 'Load event', color: '#d97706', axis: 'time' },
  // Layout shift is a unitless ratio, so it cannot share the seconds axis.
  { key: 'cls', name: 'Cumulative Layout Shift', color: '#dc2626', axis: 'ratio' },
];

// Categorical palette for the cross-page comparison chart — one colour per page.
// The sweep measures ~10-15 pages, so this is comfortably longer than that and
// cycles if ever exceeded. Picked for distinguishability at 2px line width.
const PAGE_PALETTE = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#7c3aed',
  '#db2777', '#0891b2', '#65a30d', '#ea580c', '#4f46e5',
  '#0d9488', '#c026d3', '#ca8a04', '#9333ea', '#e11d48',
  '#2dd4bf', '#f97316', '#84cc16',
];

// Cross-page comparison: pick ONE metric, see every page as its own line over
// the recent sweeps. The inverse of TrendChart (which is one page, every
// metric). Answers "which pages are slowest / regressing on <metric>?" at a
// glance. Follows the Desktop/Mobile toggle via the `profile` prop.
function ComparisonChart({ profile }) {
  const [data, setData] = useState(null); // { pages, points } from the API
  const [metric, setMetric] = useState('content_ms');
  const [hidden, setHidden] = useState({}); // page_id -> true when hidden
  const [focus, setFocus] = useState(null); // page_id being hovered in the legend

  useEffect(() => {
    let cancelled = false;
    setData(null);
    apiService
      .getPerformanceTrendAll(profile, 30)
      .then((res) => !cancelled && setData(res.data))
      .catch(() => !cancelled && setData({ pages: [], points: [] }));
    return () => {
      cancelled = true;
    };
  }, [profile]);

  const toggle = (pageId) => setHidden((prev) => ({ ...prev, [pageId]: !prev[pageId] }));

  if (data === null) {
    return (
      <div className="bg-white rounded-xl border border-gray-200/80 p-6">
        <div className="text-xs text-gray-400 py-8 text-center">Loading comparison…</div>
      </div>
    );
  }

  const pages = data.pages || [];
  const points = data.points || [];
  const isRatio = metric === 'cls';
  const sel = TREND_SERIES.find((s) => s.key === metric);

  // One row per sweep, with a column per page holding that page's value for the
  // selected metric. Pages a sweep skipped stay null (connectNulls draws through).
  const chartData = points.map((pt) => {
    const row = { when: format(new Date(pt.timestamp), 'MMM dd HH:mm') };
    pages.forEach((pg) => {
      const v = pt.metrics?.[pg.page_id]?.[metric];
      row[pg.page_id] = v == null ? null : isRatio ? Number(v.toFixed(3)) : Math.round(v);
    });
    return row;
  });

  const colorFor = (i) => PAGE_PALETTE[i % PAGE_PALETTE.length];
  const colorByPage = {};
  pages.forEach((pg, i) => { colorByPage[pg.page_id] = colorFor(i); });
  const bands = BANDS[metric]; // reference lines where Google defines them

  // Legend grouped by section (New Gear / Used Gear / Connect …), preserving the
  // area order the API already sorted by. 14 flat entries are a wall; grouped,
  // you can find and silence a whole noisy section at once.
  const legendGroups = [];
  const groupIdx = {};
  pages.forEach((pg) => {
    const area = pg.area || 'Other';
    if (groupIdx[area] == null) { groupIdx[area] = legendGroups.length; legendGroups.push({ area, pages: [] }); }
    legendGroups[groupIdx[area]].pages.push(pg);
  });
  const setMany = (ids, hide) =>
    setHidden((prev) => { const next = { ...prev }; ids.forEach((id) => { next[id] = hide; }); return next; });
  const anyHidden = pages.some((pg) => hidden[pg.page_id]);

  // Compact tooltip: a 14-row box is its own clutter. Show the slowest handful
  // at that moment and roll the rest into "+N more".
  const fmt = (v) => (isRatio ? v : ms(v));
  const CompactTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    const items = payload
      .filter((p) => p.value != null)
      .sort((a, b) => b.value - a.value);
    const top = items.slice(0, 6);
    const rest = items.length - top.length;
    return (
      <div className="bg-white border border-gray-200 rounded-md shadow-sm px-2.5 py-2 text-xs">
        <div className="text-gray-400 mb-1">{label}</div>
        {top.map((p) => (
          <div key={p.dataKey} className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.stroke }} />
            <span className="text-gray-600 truncate max-w-[180px]">{p.name}</span>
            <span className="text-gray-900 font-medium ml-auto pl-2">{fmt(p.value)}</span>
          </div>
        ))}
        {rest > 0 && <div className="text-gray-400 mt-1">+{rest} more</div>}
      </div>
    );
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200/80 p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">All pages compared</h2>
          <p className="text-xs text-gray-500 mt-0.5">
            One line per page over recent sweeps. Hover a page to isolate its line; click to hide it.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm shrink-0">
          <span className="text-gray-500">Metric</span>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="border border-gray-200 rounded-md px-2.5 py-1.5 text-sm bg-white text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary-200"
          >
            {TREND_SERIES.map((s) => (
              <option key={s.key} value={s.key}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {pages.length === 0 || chartData.length < 2 ? (
        <div className="text-xs text-gray-400 py-10 text-center">
          Not enough history yet — the comparison appears after a couple of sweeps.
        </div>
      ) : (
        <>
          {/* Legend grouped by section. Hover isolates a line; click hides it.
              Each section header hides/shows the whole group in one click. */}
          <div className="space-y-1 mb-3">
            {legendGroups.map((grp) => {
              const ids = grp.pages.map((p) => p.page_id);
              const allHidden = ids.every((id) => hidden[id]);
              return (
                <div key={grp.area} className="flex flex-wrap items-center gap-x-3.5 gap-y-1">
                  <button
                    onClick={() => setMany(ids, !allHidden)}
                    className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 hover:text-gray-600 w-[72px] text-left shrink-0"
                    title={allHidden ? `Show all ${grp.area}` : `Hide all ${grp.area}`}
                  >
                    {grp.area}
                  </button>
                  {grp.pages.map((pg) => (
                    <button
                      key={pg.page_id}
                      onClick={() => toggle(pg.page_id)}
                      onMouseEnter={() => setFocus(pg.page_id)}
                      onMouseLeave={() => setFocus(null)}
                      className={`inline-flex items-center gap-1.5 text-xs transition-opacity ${
                        hidden[pg.page_id] ? 'opacity-40 hover:opacity-75' : 'opacity-100'
                      }`}
                      title={hidden[pg.page_id] ? `Show ${pg.page_name}` : `Hide ${pg.page_name}`}
                    >
                      <span
                        className="inline-block w-3.5 h-0.5 rounded-full shrink-0"
                        style={{ backgroundColor: colorByPage[pg.page_id] }}
                      />
                      <span className={hidden[pg.page_id] ? 'text-gray-500 line-through' : 'text-gray-700'}>
                        {pg.page_name}
                      </span>
                    </button>
                  ))}
                </div>
              );
            })}
            {anyHidden && (
              <button
                onClick={() => setHidden({})}
                className="text-[11px] text-primary-600 hover:text-primary-700 mt-0.5"
              >
                Show all pages
              </button>
            )}
          </div>

          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 8, right: 8, left: -8, bottom: 0 }}>
                <XAxis dataKey="when" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={44}
                  tickFormatter={(v) => (isRatio ? v : `${(v / 1000).toFixed(0)}s`)}
                />
                <Tooltip content={<CompactTooltip />} />
                {bands && (
                  <>
                    <ReferenceLine y={bands.good} stroke="#16a34a" strokeDasharray="4 4" />
                    <ReferenceLine y={bands.poor} stroke="#dc2626" strokeDasharray="4 4" />
                  </>
                )}
                {pages.map((pg, i) =>
                  hidden[pg.page_id] ? null : (
                    <Line
                      key={pg.page_id}
                      type="monotone"
                      dataKey={pg.page_id}
                      name={pg.page_name}
                      stroke={colorFor(i)}
                      // Hovering a legend entry fades every other line so the one
                      // you're chasing stands out of the tangle.
                      strokeWidth={focus === pg.page_id ? 3 : 1.5}
                      strokeOpacity={focus && focus !== pg.page_id ? 0.1 : 1}
                      dot={false}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ),
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            {sel ? sel.name : 'Metric'} · {profile === 'mobile' ? 'Mobile (Slow 4G)' : 'Desktop (Wifi)'}
            {bands ? ' · dashed lines mark Google’s good / poor thresholds' : ''}
          </p>
        </>
      )}
    </div>
  );
}

function TrendChart({ pageId, profile }) {
  const [points, setPoints] = useState(null);
  // Times on by default; layout shift starts off because it reads on its own
  // right-hand axis and would otherwise sit as a flat line near zero.
  const [shown, setShown] = useState(() => ({
    content_ms: true, lcp_ms: true, fcp_ms: true, load_ms: true, cls: false,
  }));

  useEffect(() => {
    let cancelled = false;
    apiService
      .getPerformanceTrend(pageId, profile, 30)
      .then((res) => {
        if (cancelled) return;
        setPoints(
          res.data.map((p) => ({
            when: format(new Date(p.timestamp), 'MMM dd HH:mm'),
            content_ms: p.content_ms != null ? Math.round(p.content_ms) : null,
            lcp_ms: p.lcp_ms != null ? Math.round(p.lcp_ms) : null,
            fcp_ms: p.fcp_ms != null ? Math.round(p.fcp_ms) : null,
            load_ms: p.load_ms != null ? Math.round(p.load_ms) : null,
            cls: p.cls != null ? Number(p.cls.toFixed(3)) : null,
          })),
        );
      })
      .catch(() => !cancelled && setPoints([]));
    return () => {
      cancelled = true;
    };
  }, [pageId, profile]);

  const toggle = (key) => setShown((prev) => ({ ...prev, [key]: !prev[key] }));

  if (points === null) return <div className="text-xs text-gray-400 py-8 text-center">Loading trend…</div>;
  if (points.length < 2) {
    return (
      <div className="text-xs text-gray-400 py-8 text-center">
        Not enough history yet — the trend appears after a couple of sweeps.
      </div>
    );
  }

  const anyRatio = shown.cls;
  const anyTime = TREND_SERIES.some((sr) => sr.axis === 'time' && shown[sr.key]);

  return (
    <div>
      {/* Clickable legend. Dimmed and struck through means hidden; click to restore. */}
      <div className="flex flex-wrap gap-x-4 gap-y-1.5 mb-3">
        {TREND_SERIES.map((sr) => (
          <button
            key={sr.key}
            onClick={() => toggle(sr.key)}
            className={`inline-flex items-center gap-1.5 text-xs transition-opacity ${
              shown[sr.key] ? 'opacity-100' : 'opacity-40 hover:opacity-75'
            }`}
            title={shown[sr.key] ? `Hide ${sr.name}` : `Show ${sr.name}`}
          >
            <span
              className="inline-block w-3.5 h-0.5 rounded-full shrink-0"
              style={{ backgroundColor: sr.color }}
            />
            <span className={shown[sr.key] ? 'text-gray-700' : 'text-gray-500 line-through'}>
              {sr.name}
            </span>
            {sr.axis === 'ratio' && <span className="text-[10px] text-gray-400">right axis</span>}
          </button>
        ))}
      </div>

      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={points} margin={{ top: 8, right: anyRatio ? 4 : 8, left: -12, bottom: 0 }}>
            <XAxis dataKey="when" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
            <YAxis
              yAxisId="time"
              tick={{ fontSize: 10 }}
              tickFormatter={(v) => `${(v / 1000).toFixed(0)}s`}
              hide={!anyTime}
            />
            <YAxis yAxisId="ratio" orientation="right" tick={{ fontSize: 10 }} width={38} hide={!anyRatio} />
            <Tooltip
              formatter={(value, name) => {
                const sr = TREND_SERIES.find((x) => x.name === name);
                if (sr && sr.axis === 'ratio') return [value, name];
                return [ms(value), name];
              }}
            />
            {/* Google's good / poor lines, meaningful only for the time series. */}
            {anyTime && (
              <>
                <ReferenceLine yAxisId="time" y={BANDS.lcp_ms.good} stroke="#16a34a" strokeDasharray="4 4" />
                <ReferenceLine yAxisId="time" y={BANDS.lcp_ms.poor} stroke="#dc2626" strokeDasharray="4 4" />
              </>
            )}
            {TREND_SERIES.filter((sr) => shown[sr.key]).map((sr) => (
              <Line
                key={sr.key}
                yAxisId={sr.axis}
                type="monotone"
                dataKey={sr.key}
                name={sr.name}
                stroke={sr.color}
                strokeWidth={2}
                dot={false}
                connectNulls
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// --- Network timeline ---------------------------------------------------
// The per-request waterfall: one bar per file, split into the phases the
// browser reports, laid against the page's own timeline with markers for first
// byte, DOMContentLoaded and load.
const PHASES = [
  { key: 'dns', name: 'DNS', color: '#ec4899' },
  { key: 'ssl', name: 'SSL', color: '#a855f7' },
  { key: 'connect', name: 'Connect', color: '#3b82f6' },
  { key: 'send', name: 'Send', color: '#f97316' },
  { key: 'wait', name: 'Wait', color: '#eab308' },
  { key: 'receive', name: 'Receive', color: '#22c55e' },
];

const MARKERS = [
  { key: 'first_byte', name: 'First byte received', color: '#2563eb' },
  { key: 'dom_content_loaded', name: 'DOM content loaded', color: '#f59e0b' },
  { key: 'on_load', name: 'On load', color: '#16a34a' },
];

const TYPE_ICON = {
  document: 'HTML', script: 'JS', css: 'CSS', link: 'CSS', img: 'IMG',
  image: 'IMG', font: 'FONT', fetch: 'XHR', xmlhttprequest: 'XHR', other: '—',
};

function NetworkTimeline({ pageId, profile }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState('load');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    apiService
      .getPerformanceWaterfall(pageId, profile)
      .then((res) => !cancelled && setData(res.data))
      .catch((e) => !cancelled && setError(e.response?.status === 404
        ? 'No network timeline recorded yet — it appears after the next sweep.'
        : (e.response?.data?.detail || e.message)));
    return () => { cancelled = true; };
  }, [pageId, profile]);

  if (error) return <div className="text-xs text-gray-400 py-6 text-center">{error}</div>;
  if (!data) return <div className="text-xs text-gray-400 py-6 text-center">Loading network timeline…</div>;

  const scaleEnd = Math.max(
    data.markers?.on_load || 0,
    ...data.rows.map((r) => r.end || 0),
  ) || 1;

  const rows = data.rows
    .filter((r) => !filter || (r.file + ' ' + r.host).toLowerCase().includes(filter.toLowerCase()))
    .slice()
    .sort((a, b) => {
      if (sort === 'size') return (b.kb || 0) - (a.kb || 0);
      if (sort === 'duration') return (b.duration || 0) - (a.duration || 0);
      return a.start - b.start;
    });

  const pct = (v) => `${Math.max(0, Math.min(100, (v / scaleEnd) * 100))}%`;
  // Six evenly spaced time labels across the top.
  const ticks = Array.from({ length: 6 }, (_, i) => (scaleEnd / 5) * i);

  return (
    <div>
      {/* Legend + controls */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-3">
        {PHASES.map((p) => (
          <span key={p.key} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
            {p.name}
          </span>
        ))}
        {MARKERS.map((m) => (
          <span key={m.key} className="inline-flex items-center gap-1.5 text-xs text-gray-600">
            <span className="w-0.5 h-3" style={{ backgroundColor: m.color }} />
            {m.name}
          </span>
        ))}
        <div className="ml-auto flex items-center gap-2">
          <select
            value={sort}
            onChange={(e) => setSort(e.target.value)}
            className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white"
          >
            <option value="load">Sort by load order</option>
            <option value="size">Sort by size</option>
            <option value="duration">Sort by duration</option>
          </select>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            className="text-xs border border-gray-300 rounded-md px-2 py-1 w-32"
          />
        </div>
      </div>

      <div className="text-[11px] text-gray-400 mb-1">
        {data.total_requests} requests
        {data.truncated && ' (showing the first 300)'}
        {rows.length !== data.rows.length && ` · ${rows.length} matching filter`}
      </div>

      {/* Header: file / size / time axis */}
      <div className="flex items-center text-[10px] text-gray-400 border-b border-gray-200 pb-1 mb-1">
        <div className="w-56 shrink-0">FILE / PATH</div>
        <div className="w-16 shrink-0 text-right pr-3">SIZE</div>
        <div className="relative flex-1 h-4">
          {ticks.map((t, i) => (
            <span key={i} className="absolute -translate-x-1/2" style={{ left: pct(t) }}>
              {(t / 1000).toFixed(1)}s
            </span>
          ))}
        </div>
      </div>

      <div className="max-h-[28rem] overflow-y-auto">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center text-[11px] hover:bg-gray-50 group/row">
            <div className="w-56 shrink-0 truncate py-1" title={r.url}>
              <span className="inline-block w-8 text-[9px] text-gray-400 uppercase">
                {TYPE_ICON[r.type] || r.type.slice(0, 4)}
              </span>
              <span className="text-gray-700">{r.file}</span>
            </div>
            <div className="w-16 shrink-0 text-right pr-3 text-gray-500">
              {r.cached ? 'cached' : r.kb ? `${r.kb} KB` : '—'}
            </div>
            <div className="relative flex-1 h-5">
              {/* Marker lines behind the bars */}
              {MARKERS.map((m) =>
                data.markers?.[m.key] ? (
                  <span
                    key={m.key}
                    className="absolute top-0 bottom-0 w-px opacity-60"
                    style={{ left: pct(data.markers[m.key]), backgroundColor: m.color }}
                  />
                ) : null,
              )}
              {/* The request bar, positioned at its start time */}
              <div
                className="absolute top-1 h-3 flex rounded-sm overflow-hidden"
                style={{ left: pct(r.start), width: pct(Math.max(r.end - r.start, scaleEnd * 0.002)) }}
                title={
                  r.opaque
                    ? `${r.file}\n${r.duration}ms total — this server does not share its timing breakdown`
                    : `${r.file}\n` + PHASES.map((p) => `${p.name}: ${r[p.key]}ms`).join('\n')
                }
              >
                {r.opaque ? (
                  // No Timing-Allow-Origin: we only honestly know start and end.
                  <div className="h-full w-full bg-gray-300" />
                ) : (
                  PHASES.map((p) => {
                    const span = r[p.key] || 0;
                    if (span <= 0) return null;
                    const w = (span / Math.max(r.end - r.start, 0.1)) * 100;
                    return <div key={p.key} style={{ width: `${w}%`, backgroundColor: p.color }} />;
                  })
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="text-[11px] text-gray-400 mt-2">
        Grey bars are third-party servers that do not send a
        <code className="mx-1 text-gray-500">Timing-Allow-Origin</code>
        header, so the browser withholds their phase breakdown — only start and end are known.
      </div>
    </div>
  );
}

const BAND_RANK = { good: 0, ok: 1, poor: 2, unknown: -1 };

function PageCard({ row }) {
  const [open, setOpen] = useState(false);
  // Grade on the worse of the browser's largest-item time and our readiness
  // measure. Grading on the largest item alone would show "Good" for a page
  // whose one big photo painted quickly while the content a visitor came for
  // was still seconds away.
  const lcpBand = band('lcp_ms', row.lcp_ms);
  const readyBand = band('lcp_ms', row.content_ms);
  const b = BAND_RANK[readyBand] > BAND_RANK[lcpBand] ? readyBand : lcpBand;
  const failed = row.runs_ok === 0;

  return (
    <div className="bg-white rounded-xl border border-gray-200/80 overflow-hidden">
      <div className="p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-semibold text-gray-900 truncate">{row.page_name}</h3>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${BAND_PILL[failed ? 'poor' : b]}`}>
                {failed ? 'Did not load' : BAND_LABEL[b]}
              </span>
            </div>
            <a
              href={row.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-primary-600 inline-flex items-center gap-1 mt-0.5 break-all"
            >
              {row.url}
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          </div>
          <button
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1"
          >
            {open ? 'Hide' : 'Details, trend & network timeline'}
            <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </div>

        {failed && row.error_message && (
          <div className="mt-3 text-xs text-error-700 bg-error-50 rounded-md p-2 flex gap-2">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span className="break-all">{row.error_message}</span>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 gap-y-5 mt-4">
          <Metric
            label={row.content_label || 'Time until the main content is visible'}
            term="Content readiness"
            value={row.content_ms}
            measured={row.content_element_desc}
            definition={
              <>
                <strong>Content readiness.</strong> Measured from the instant the page starts
                loading until the thing this page exists for is actually on screen. Unlike the two
                paint measurements above, it is specific to what this page is for — which is why it
                is named differently on each page.
                <br />
                <br />
                {row.content_means || 'This page does not define a readiness element.'}
                {row.content_element_desc && (
                  <>
                    <br />
                    <br />
                    On this run it waited for:{' '}
                    <span className="text-primary-200">{row.content_element_desc}</span>
                  </>
                )}
              </>
            }
          />
          <Metric
            label="Time until the biggest single item appears"
            term="Largest Contentful Paint · Google's ranking score"
            value={row.lcp_ms}
            metric="lcp_ms"
            hint="good under 2.5s · poor over 4s"
            measured={row.lcp_element_desc}
            definition={
              <>
                <strong>Largest Contentful Paint.</strong> The moment the single biggest image or
                block of text in the visible area has finished rendering. Google treats this as the
                point a visitor feels the page has loaded, and it counts towards search ranking.
                <br />
                <br />
                <span className="text-gray-400">
                  Why only one item? Because that is the standard&apos;s definition, and Chrome
                  computes it — we cannot widen it. Its value is that it is the identical number
                  Google Search Console and PageSpeed Insights report for this page, so it is worth
                  keeping exactly as the standard defines it. On a listing page several product
                  photos are the same size and tie for largest; the one named below is the
                  browser&apos;s pick from that tie. To answer &ldquo;is the whole row ready&rdquo;,
                  read the readiness measure on this card instead — that one we do control, and it
                  waits for every photo it names.
                </span>
                <br />
                <br />
                {row.lcp_element_desc ? (
                  <>
                    On this run the browser timed:
                    <br />
                    <span className="text-primary-200">{row.lcp_element_desc}</span>
                  </>
                ) : (
                  'The browser did not report a largest element for this page on this run.'
                )}
              </>
            }
          />
          <Metric
            label="Time until the screen is not blank"
            term="First Contentful Paint"
            value={row.fcp_ms}
            metric="fcp_ms"
            hint="good under 1.8s · poor over 3s"
            definition={
              <>
                <strong>First Contentful Paint.</strong> The moment anything at all appears — the
                first piece of text or the first image. Before this the visitor is looking at a
                blank white screen. It does not tell you the page is usable, only that something
                has arrived.
              </>
            }
          />
          <Metric
            label="How much the page jumps"
            term="Cumulative Layout Shift"
            value={row.cls}
            metric="cls"
            hint="good under 0.1 · poor over 0.25"
            measured={row.cls_worst_element}
            format={(v) => (v == null ? '—' : v.toFixed(3))}
            definition={
              <>
                <strong>Cumulative Layout Shift.</strong> A score for how much content moves around
                on its own while the page loads — the reason a shopper taps the wrong thing because
                a banner or image pushed the page down underneath their finger. It is a ratio, not a
                time: 0 is rock steady.
                <br />
                <br />
                {row.cls_worst_element ? (
                  <>
                    Biggest single shift here was{' '}
                    <span className="text-primary-200">{(row.cls_worst_value ?? 0).toFixed(3)}</span>, caused by:
                    <br />
                    <span className="text-primary-200">{row.cls_worst_element}</span>
                  </>
                ) : (
                  'No significant shift was attributed to a specific element on this run.'
                )}
              </>
            }
          />
          <Metric
            label="Time until the page's own files arrive"
            term="Load event · not the same as ready"
            value={row.load_ms}
            hint={row.load_event_fired ? undefined : 'never finished'}
            definition={
              <>
                <strong>The load event.</strong> The browser fires this once the HTML document and
                the files that document itself asks for — scripts, stylesheets, fonts, images
                written into the HTML — have all arrived.
                <br />
                <br />
                <span className="text-gray-400">
                  It is <em>not</em> a ceiling, and it does not mean the visitor can use the page.
                  On app-style pages the HTML is nearly empty, so the browser calls it loaded as
                  soon as the code arrives, and the real content is fetched afterwards. Measured on
                  Connect: the load event fired at 2.4s with zero musician photos and 129 characters
                  of text on the page; the first photo appeared at 6.3s. That is why readiness can
                  legitimately be several seconds larger than this number.
                  <br />
                  <br />
                  On the store pages the two track closely, because Shopify sends real HTML with
                  the products already in it.
                </span>
                {!row.load_event_fired && (
                  <>
                    <br />
                    <br />
                    <span className="text-primary-200">
                      On this page the load event never fired within the time allowed — something
                      keeps requesting indefinitely. The other measures above are still valid.
                    </span>
                  </>
                )}
              </>
            }
          />
          <Metric
            label="Total downloaded"
            term="Page weight"
            value={row.weight_kb}
            format={kb}
            hint={`${row.requests ?? '—'} requests`}
            definition={
              <>
                <strong>Page weight.</strong> Every byte the browser pulled down to render this
                page — HTML, JavaScript, stylesheets, images, fonts and third-party tags — up to the
                point the page finished loading. On a slow mobile connection this is the number that
                decides how long the wait is.
                {row.heaviest_asset_url && (
                  <>
                    <br />
                    <br />
                    The single biggest file on this page is{' '}
                    <span className="text-primary-200">{kb(row.heaviest_asset_kb)}</span> —{' '}
                    {friendlyFile(row.heaviest_asset_url)}.
                  </>
                )}
              </>
            }
          />
        </div>
      </div>

      {open && (
        <div className="border-t border-gray-100 bg-gray-50/60 p-4 sm:p-5 space-y-5">
          <div>
            <div className="text-xs font-semibold text-gray-600 mb-2">Trend over recent sweeps</div>
            <TrendChart pageId={row.page_id} profile={row.profile} />
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-600 mb-2">Network timeline</div>
            <NetworkTimeline pageId={row.page_id} profile={row.profile} />
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-600 mb-2">Diagnostics</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Metric label="Server first response" term="Time To First Byte" value={row.ttfb_ms} />
              <Metric label="Browser was frozen for" term="Total Blocking Time" value={row.tbt_ms} />
              <Metric label="Downloaded from third parties" term="Third-party weight" value={row.third_party_kb} format={kb} />
            </div>
            {!row.load_event_fired && (
              <div className="mt-3 text-xs text-warning-700 bg-warning-50 rounded-md p-2">
                The page never finished loading within the budget — usually third-party tags
                that keep requesting indefinitely. The paint numbers above are still valid.
              </div>
            )}
            {row.heaviest_asset_url && (
              <div className="mt-3 text-xs text-gray-600">
                <span className="font-medium">Biggest single file:</span>{' '}
                <span className="font-semibold">{kb(row.heaviest_asset_kb)}</span>
                {' — '}
                {friendlyFile(row.heaviest_asset_url)}
              </div>
            )}
            {row.lcp_element_selector && (
              <details className="mt-3">
                <summary className="cursor-pointer text-xs text-gray-400 select-none">
                  For developers — exact element references
                </summary>
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  <div>
                    <span className="font-medium">Largest element:</span>{' '}
                    <span className="font-mono break-all text-gray-500">{row.lcp_element_selector}</span>
                  </div>
                  {row.lcp_element_url && (
                    <div className="font-mono break-all text-gray-400">{row.lcp_element_url}</div>
                  )}
                  {row.content_selector && (
                    <div>
                      <span className="font-medium">Readiness waits on:</span>{' '}
                      <span className="font-mono break-all text-gray-500">{row.content_selector}</span>
                    </div>
                  )}
                  {row.heaviest_asset_url && (
                    <div className="font-mono break-all text-gray-400">{row.heaviest_asset_url}</div>
                  )}
                </div>
              </details>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold text-gray-600 mb-2">After the shopper scrolls</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <Metric label="Weight after scroll" value={row.scrolled_weight_kb} format={kb} />
              <Metric
                label="Requests after scroll"
                value={row.scrolled_requests}
                format={(v) => (v == null ? '—' : String(v))}
              />
              <Metric
                label="Jumpiness after scroll"
                value={row.scrolled_cls}
                metric="cls"
                format={(v) => (v == null ? '—' : v.toFixed(3))}
              />
              <Metric
                label="Lazy images settled"
                value={row.lazy_settle_ms}
                hint={row.lazy_settle_ms == null ? 'never settled' : undefined}
              />
            </div>
            {row.scrolled_images_unloaded > 0 && (
              <div className="mt-3 text-xs text-warning-700 bg-warning-50 rounded-md p-2">
                {row.scrolled_images_unloaded} image(s) never loaded, even after scrolling to them.
              </div>
            )}
          </div>

          <div className="text-[11px] text-gray-400">
            Median of {row.runs_attempted} run(s) · {row.runs_ok} succeeded ·
            measured {format(new Date(row.timestamp), 'MMM dd, yyyy HH:mm')}
          </div>
        </div>
      )}
    </div>
  );
}

const Performance = () => {
  const [rows, setRows] = useState([]);
  const [profile, setProfile] = useState('desktop');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [notice, setNotice] = useState(null);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await apiService.getPerformanceLatest();
      setRows(res.data);
      setError(null);
      return res.data;
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
    const interval = setInterval(fetchLatest, 120000);
    return () => clearInterval(interval);
  }, [fetchLatest]);

  const triggerSweep = async () => {
    setTriggering(true);
    setNotice(null);
    const beforeTs = rows.length ? new Date(rows[0].timestamp).getTime() : 0;

    try {
      await apiService.triggerPerformanceSweep();
    } catch (err) {
      // 409 means a test run is in flight — the sweep would skew both.
      setNotice(err.response?.data?.detail || err.message);
      setTriggering(false);
      return;
    }

    setNotice('Sweep started. It measures every page several times, so this takes a while.');
    const deadline = Date.now() + 45 * 60 * 1000;
    while (Date.now() < deadline) {
      await new Promise((r) => setTimeout(r, 15000));
      const data = await fetchLatest();
      if (data && data.length && new Date(data[0].timestamp).getTime() > beforeTs) {
        setNotice(null);
        break;
      }
    }
    setTriggering(false);
  };

  const visible = rows.filter((r) => r.profile === profile);
  const areas = [...new Set(visible.map((r) => r.area || 'Other'))];
  const worst = visible.filter(
    (r) => band('lcp_ms', r.lcp_ms) === 'poor' || band('lcp_ms', r.content_ms) === 'poor' || r.runs_ok === 0,
  );
  // The newest measurement across all pages — a targeted sweep updates only
  // some rows, so the first row in sort order is not necessarily the latest.
  const measuredAt = rows.length
    ? new Date(Math.max(...rows.map((r) => new Date(r.timestamp).getTime())))
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <RefreshCw className="h-5 w-5 animate-spin mr-2" />
        Loading page performance…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Gauge className="h-6 w-6 text-primary-600" />
            Page Speed
          </h1>
          <p className="text-gray-500 mt-1 text-sm max-w-2xl">
            How long a shopper actually waits, measured in a real browser. This is different
            from Health Checks, which only time the server's first response and never load
            the page.
          </p>
          {measuredAt && (
            <p className="text-xs text-gray-400 mt-1">
              Last measured {format(measuredAt, 'MMM dd, yyyy HH:mm')}
            </p>
          )}
        </div>
        <button
          onClick={triggerSweep}
          disabled={triggering}
          className="btn-primary inline-flex items-center gap-2 shrink-0 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${triggering ? 'animate-spin' : ''}`} />
          {triggering ? 'Measuring…' : 'Run Sweep'}
        </button>
      </div>

      {notice && (
        <div className="text-sm bg-warning-50 text-warning-800 rounded-lg p-3">{notice}</div>
      )}
      {error && (
        <div className="text-sm bg-error-50 text-error-700 rounded-lg p-3">{error}</div>
      )}

      {/* Desktop / mobile toggle. Mobile is throttled to Slow 4G, which is where
          the real problems show up — desktop alone hides them completely. */}
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {[
          { key: 'desktop', label: 'Desktop (Wifi)', Icon: Monitor },
          { key: 'mobile', label: 'Mobile (Slow 4G)', Icon: Smartphone },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setProfile(key)}
            className={`px-3 py-1.5 text-sm rounded-md inline-flex items-center gap-1.5 ${
              profile === key ? 'bg-primary-600 text-white' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </button>
        ))}
      </div>

      {!rows.length ? (
        <div className="bg-white rounded-xl border border-gray-200/80 p-10 text-center text-gray-500">
          No measurements yet. Hit <span className="font-medium">Run Sweep</span> to take the first one.
        </div>
      ) : !visible.length ? (
        <div className="bg-white rounded-xl border border-gray-200/80 p-10 text-center text-gray-500">
          The last sweep didn't include this device profile. Enable it in Settings, or run a new sweep.
        </div>
      ) : (
        <>
          <ComparisonChart profile={profile} />

          {worst.length > 0 && (
            <div className="bg-error-50 border border-error-100 rounded-xl p-4">
              <div className="font-semibold text-error-800 text-sm">
                {worst.length} page(s) are slow enough to cost sales on {profile === 'mobile' ? 'mobile' : 'desktop'}
              </div>
              <ul className="mt-2 space-y-1 text-sm text-error-700">
                {worst.map((r) => (
                  <li key={`${r.page_id}-${r.profile}`}>
                    {r.page_name} —{' '}
                    {r.runs_ok === 0
                      ? 'did not load'
                      : band('lcp_ms', r.content_ms) === 'poor'
                        ? `${ms(r.content_ms)} before ${(r.content_label || 'content is ready').toLowerCase().replace(/^time until /, '')}`
                        : `${ms(r.lcp_ms)} before its biggest item appears`}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {areas.map((area) => (
            <div key={area} className="space-y-3">
              <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{area}</h2>
              {visible
                .filter((r) => (r.area || 'Other') === area)
                .map((r) => (
                  <PageCard key={`${r.page_id}-${r.profile}`} row={r} />
                ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
};

export default Performance;
